import {
  Target, Sprout, Trophy, Sword, Crown, Coffee, Shield, Medal, Zap, Star, Gem, Award,
  Footprints, Smile, Sliders, Calendar, Sun, Heart, RefreshCw, Eye, TrendingUp,
  Repeat, CheckCircle, Bookmark, Globe, Moon,
  type LucideIcon
} from "lucide-react";
import { cn } from "@/lib/utils";

const iconMap: Record<string, LucideIcon> = {
  Target, Sprout, Trophy, Sword, Crown, Coffee, Shield, Medal, Zap, Star, Gem, Award,
  Footprints, Smile, Sliders, Calendar, Sun, Heart, RefreshCw, Eye, TrendingUp,
  Repeat, CheckCircle, Bookmark, Globe, Moon,
};

const colorThemes: Record<string, { bg: string; icon: string }> = {
  Target: { bg: "bg-blue-500/15", icon: "text-blue-500" },
  Sprout: { bg: "bg-green-500/15", icon: "text-green-500" },
  Trophy: { bg: "bg-amber-500/15", icon: "text-amber-500" },
  Sword: { bg: "bg-red-500/15", icon: "text-red-500" },
  Crown: { bg: "bg-yellow-500/15", icon: "text-yellow-500" },
  Coffee: { bg: "bg-orange-500/15", icon: "text-orange-500" },
  Shield: { bg: "bg-indigo-500/15", icon: "text-indigo-500" },
  Medal: { bg: "bg-emerald-500/15", icon: "text-emerald-500" },
  Zap: { bg: "bg-purple-500/15", icon: "text-purple-500" },
  Star: { bg: "bg-yellow-400/15", icon: "text-yellow-400" },
  Gem: { bg: "bg-pink-500/15", icon: "text-pink-500" },
  Award: { bg: "bg-primary/15", icon: "text-primary" },
  Footprints: { bg: "bg-sky-500/15", icon: "text-sky-500" },
  Smile: { bg: "bg-amber-400/15", icon: "text-amber-400" },
  Sliders: { bg: "bg-violet-500/15", icon: "text-violet-500" },
  Calendar: { bg: "bg-blue-600/15", icon: "text-blue-600" },
  Sun: { bg: "bg-orange-400/15", icon: "text-orange-400" },
  Heart: { bg: "bg-rose-500/15", icon: "text-rose-500" },
  RefreshCw: { bg: "bg-teal-500/15", icon: "text-teal-500" },
  Eye: { bg: "bg-cyan-500/15", icon: "text-cyan-500" },
  TrendingUp: { bg: "bg-lime-500/15", icon: "text-lime-500" },
  Repeat: { bg: "bg-fuchsia-500/15", icon: "text-fuchsia-500" },
  CheckCircle: { bg: "bg-emerald-500/15", icon: "text-emerald-500" },
  Bookmark: { bg: "bg-indigo-400/15", icon: "text-indigo-400" },
  Globe: { bg: "bg-sky-600/15", icon: "text-sky-600" },
  Moon: { bg: "bg-slate-500/15", icon: "text-slate-500" },
};

interface BadgeIconProps {
  iconName: string;
  size?: "sm" | "md" | "lg" | "xl";
  showBackground?: boolean;
  isLocked?: boolean;
  className?: string;
}

const sizeClasses = {
  sm: { container: "w-8 h-8", icon: "w-4 h-4" },
  md: { container: "w-12 h-12", icon: "w-6 h-6" },
  lg: { container: "w-16 h-16", icon: "w-8 h-8" },
  xl: { container: "w-20 h-20", icon: "w-10 h-10" },
};

export function BadgeIcon({
  iconName,
  size = "lg",
  showBackground = true,
  isLocked = false,
  className,
}: BadgeIconProps) {
  const IconComponent = iconMap[iconName] || Award;
  const theme = colorThemes[iconName] || colorThemes.Award;
  const sizeConfig = sizeClasses[size];

  return (
    <div
      className={cn(
        "rounded-full flex items-center justify-center",
        showBackground && theme.bg,
        sizeConfig.container,
        isLocked && "opacity-50",
        className
      )}
    >
      <IconComponent
        className={cn(
          sizeConfig.icon,
          isLocked ? "text-muted-foreground" : theme.icon
        )}
      />
    </div>
  );
}

export function getBadgeIconComponent(iconName: string): LucideIcon {
  return iconMap[iconName] || Award;
}
